import { ArrowRightIcon, FilterIcon } from 'assets/icons';
import { FC, useState } from 'react';
import { VoidFn } from 'types/baseTypes';
import {
  HELD_IN_RESERVE_DATE_OPTIONS,
  HeldInReserveStatus,
  PAYOUT_TXNS_AMOUNT_FILTERS,
  PAYOUT_TXNS_HELD_IN_RESERVE_TABS
} from 'containers/payouts/constants';
import { CustomButton } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import AmountFilter from 'containers/payouts/components/AmountFilterBox';
import DateFilterGroup from 'components/date-filter-group/DateFilterGroup';
import { constructDateRange } from 'containers/payouts/utils';
import { AmountFilterValue, DateFilter } from 'containers/payouts/payouts.model';

interface HeldInReserveFiltersProps {
  handleClose: VoidFn;
  onApply: (filter) => void;
  appliedAmountFilter: AmountFilterValue;
  appliedCreatedDateFilter: DateFilter;
  appliedAvailableDateFilter: DateFilter;
  appliedStatus: string;
  selectedTab: PAYOUT_TXNS_HELD_IN_RESERVE_TABS;
}

const HeldInReserveFilters: FC<HeldInReserveFiltersProps> = ({
  handleClose,
  onApply,
  appliedAmountFilter,
  appliedCreatedDateFilter,
  appliedAvailableDateFilter,
  appliedStatus,
  selectedTab
}) => {
  const [amountFilter, setAmountFilter] = useState({
    title: appliedAmountFilter.title || PAYOUT_TXNS_AMOUNT_FILTERS.ALL,
    min: appliedAmountFilter.min || '',
    max: appliedAmountFilter.max || ''
  });

  const [createdDateFilter, setCreatedDateFilter] = useState<DateFilter | null>(appliedCreatedDateFilter);
  const [availableDateFilter, setAvailableDateFilter] = useState<DateFilter | null>(appliedAvailableDateFilter);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(appliedStatus);

  const onApplyFilters = () => {
    onApply({
      amount: amountFilter,
      createdDateFilter,
      availableDateFilter,
      status: selectedStatus
    });
  };

  const handleCreatedDateClick = (option: string) => {
    const dateRange = constructDateRange(option);
    const dateFilter = { option, startDate: dateRange.startDate, endDate: dateRange.endDate };
    setCreatedDateFilter(dateFilter);
  };

  const handleAvailableDateClick = (option: string) => {
    const dateRange = constructDateRange(option);
    const dateFilter = { option, startDate: dateRange.startDate, endDate: dateRange.endDate };
    setAvailableDateFilter(dateFilter);
  };

  const applyBtnDisabled = () => {
    if (amountFilter.title !== PAYOUT_TXNS_AMOUNT_FILTERS.ALL) {
      if (amountFilter.title === PAYOUT_TXNS_AMOUNT_FILTERS.IN_BETWEEN) {
        if (+amountFilter.max <= +amountFilter.min) {
          return true;
        }
        return false;
      } else if (!amountFilter.min) {
        return true;
      }
      return false;
    }
    return false;
  };

  return (
    <div className="flex h-full w-96 flex-col">
      <div className="flex items-center gap-2 px-4 pt-6">
        <div onClick={handleClose} className="cursor-pointer">
          <ArrowRightIcon className="rotate-180" />
        </div>
        <div className="flex items-center gap-2.5 text-17px font-semibold text-headingGray">
          <FilterIcon />
          Filter
        </div>
      </div>
      <div className="mx-4 mt-4 mb-5 border-b border-borderGray"></div>

      <div className="customNormalScroll relative overflow-auto px-4">
        <div className="text-sbase font-semibold text-primaryText">By amount</div>
        <AmountFilter selectedFilter={amountFilter} onFilterChange={setAmountFilter} />
        {selectedTab === PAYOUT_TXNS_HELD_IN_RESERVE_TABS.ALL && (
          <>
            <div className="mt-5 text-sbase font-semibold text-primaryText">By status</div>
            <div className="mt-2 flex flex-wrap gap-2.5">
              {Object.values(HeldInReserveStatus).map(option => (
                <button
                  key={option}
                  onClick={() => setSelectedStatus(option)}
                  className={`rounded-full border px-5 py-1 text-sbase capitalize text-primaryText transition-colors ${
                    selectedStatus === option
                      ? 'border-secondary bg-secondaryBtn'
                      : 'border-borderGray hover:bg-gray-50'
                  }`}>
                  {option.toLowerCase()}
                </button>
              ))}
            </div>
          </>
        )}
        <DateFilterGroup
          title="Created on"
          options={HELD_IN_RESERVE_DATE_OPTIONS.CREATED}
          selectedOption={createdDateFilter?.option}
          onOptionClick={handleCreatedDateClick}
          dateRange={createdDateFilter}
          onDateRangeChange={setCreatedDateFilter}
          dateRangeKey="created"
        />

        <DateFilterGroup
          title="Available on"
          options={HELD_IN_RESERVE_DATE_OPTIONS.AVAILABLE}
          selectedOption={availableDateFilter?.option}
          onOptionClick={handleAvailableDateClick}
          dateRange={availableDateFilter}
          onDateRangeChange={setAvailableDateFilter}
          dateRangeKey="available"
          allowPastDate={false}
        />
      </div>

      <div className="mt-auto flex gap-5 bg-secondaryBtn px-4 py-2.5">
        <CustomButton
          type={ButtonType.SECONDARY}
          id="held-in-reserve-filter-cancel"
          className="h-[38px] w-1/2"
          onClick={handleClose}>
          Cancel
        </CustomButton>
        <CustomButton
          type={ButtonType.PRIMARY}
          id="held-in-reserve-filter-apply"
          className="h-[38px] w-1/2"
          onClick={onApplyFilters}
          disabled={applyBtnDisabled()}>
          Apply
        </CustomButton>
      </div>
    </div>
  );
};

export default HeldInReserveFilters;
